import { Search, ArrowRight, ShoppingBag, FileText, Navigation, Sparkles } from "lucide-react";

const CommandRow = ({ icon: Icon, label, hint, active }) => (
  <div
    className={`flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
      active
        ? "bg-indigo-500/15 border border-indigo-500/30 shadow-[0_0_24px_rgba(99,102,241,0.15)]"
        : "border border-transparent hover:bg-white/[0.04]"
    }`}
  >
    <div className="flex items-center gap-3">
      <div
        className={`h-8 w-8 rounded-md flex items-center justify-center ${
          active ? "bg-indigo-500/20 text-indigo-300" : "bg-white/[0.04] text-zinc-400"
        }`}
      >
        <Icon className="h-4 w-4" strokeWidth={1.5} />
      </div>
      <div className="font-body text-sm text-zinc-200">{label}</div>
    </div>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{hint}</div>
  </div>
);

const BrowserMockup = () => {
  return (
    <div className="relative rounded-2xl border border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] overflow-hidden">
      {/* browser chrome */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06] bg-white/[0.02]">
        <div className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-full bg-[#ff5f57]/80" />
          <span className="h-3 w-3 rounded-full bg-[#febc2e]/80" />
          <span className="h-3 w-3 rounded-full bg-[#28c840]/80" />
        </div>
        <div className="ml-4 flex-1 flex items-center gap-2 rounded-md bg-white/[0.04] px-3 py-1.5 border border-white/[0.05]">
          <div className="h-1.5 w-1.5 rounded-full bg-emerald-400/80" />
          <span className="font-mono text-[11px] text-zinc-400">equallayer.app / shop.example.com</span>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hidden sm:block">
          Layer · Active
        </div>
      </div>

      {/* content area */}
      <div className="grid md:grid-cols-[1.1fr_1fr]">
        {/* underlying 'website' preview */}
        <div className="relative p-8 md:p-10 border-b md:border-b-0 md:border-r border-white/[0.06] bg-[radial-gradient(ellipse_at_top_left,rgba(99,102,241,0.08),transparent_55%)]">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">
            hoodies · under £50
          </div>
          <div className="space-y-4 opacity-80">
            <div className="h-3 w-24 rounded-full bg-white/10" />
            <div className="h-8 w-56 rounded-full bg-white/[0.08]" />
            <div className="h-3 w-40 rounded-full bg-white/[0.06]" />
            <div className="grid grid-cols-2 gap-3 pt-4">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`aspect-[4/3] rounded-lg border border-white/[0.06] bg-gradient-to-br from-zinc-800/80 to-zinc-950/80 ${
                    i === 1 ? "ring-1 ring-indigo-500/60 shadow-[0_0_40px_rgba(99,102,241,0.25)]" : ""
                  }`}
                />
              ))}
            </div>
          </div>

          {/* highlight annotation */}
          <div className="absolute top-[62%] left-[56%] hidden md:flex items-center gap-2 animate-fade-up">
            <div className="h-px w-10 bg-gradient-to-r from-transparent to-indigo-400" />
            <div className="rounded-md border border-indigo-500/40 bg-[#0A0A0A] px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-indigo-300">
              Selected · £42
            </div>
          </div>
        </div>

        {/* command palette */}
        <div className="p-5 md:p-6 bg-[#070707]">
          <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5">
            <Search className="h-4 w-4 text-indigo-300" strokeWidth={1.5} />
            <div className="font-body text-sm text-zinc-200">
              Find a black hoodie under £50
              <span className="inline-block ml-0.5 h-4 w-[2px] bg-indigo-400 align-middle animate-blink" />
            </div>
            <div className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">⌘K</div>
          </div>

          <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 px-1">
            Equal is working
          </div>
          <div className="mt-2 flex flex-col gap-1.5">
            <CommandRow icon={Sparkles} label="Understand the request" hint="intent" active />
            <CommandRow icon={Search} label="Find matching hoodies · 3 results" hint="search" />
            <CommandRow icon={ShoppingBag} label="Select · Heavyweight Hoodie · £42" hint="act" />
            <CommandRow icon={Navigation} label="Add to cart · confirm aloud" hint="done" />
            <CommandRow icon={FileText} label="Read result to screen reader" hint="a11y" />
          </div>

          <button
            type="button"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 px-4 py-2.5 text-sm font-medium text-white transition-all"
            data-testid="mockup-run-command"
          >
            Run command
            <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrowserMockup;
